/**
 * EarningsAndPayout.jsx — Phase 3 Scribe Profile tab.
 *
 * 11-field withdrawal form per spec + payout history list. Renders as a
 * self-contained tab content panel (parent page wraps it in a Tabs shell).
 *
 * Server-side is authoritative for every validation (min 500, max 50k,
 * balance check, method-specific fields). Client-side catches the obvious
 * mistakes early so the inline errors land fast.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Coins, CreditCard, Wallet, CheckCircle2, Clock, Hourglass, XCircle,
  Banknote, AlertCircle, RefreshCw, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MIN_COINS = 500;
const MAX_COINS = 50_000;

const STATUS_META = {
  submitted: { label: 'Submitted', icon: Clock,     colour: '#94A3B8', bg: 'bg-slate-500/15',   border: 'border-slate-500/40',   text: 'text-slate-200' },
  pending:   { label: 'Pending',   icon: Hourglass, colour: '#F59E0B', bg: 'bg-amber-500/15',   border: 'border-amber-500/40',   text: 'text-amber-200' },
  approved:  { label: 'Approved',  icon: CheckCircle2, colour: '#3B82F6', bg: 'bg-blue-500/15',  border: 'border-blue-500/40',    text: 'text-blue-200' },
  paid:      { label: 'Paid Out',  icon: Banknote,  colour: '#10B981', bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', text: 'text-emerald-200' },
  rejected:  { label: 'Rejected',  icon: XCircle,   colour: '#EF4444', bg: 'bg-red-500/15',     border: 'border-red-500/40',     text: 'text-red-200' },
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.submitted;
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${m.bg} ${m.border} ${m.text}`}
      data-testid={`payout-status-${status}`}
    >
      <Icon className="w-3 h-3" />
      {m.label}
    </span>
  );
};

const EarningsAndPayout = () => {
  const [balance, setBalance] = useState({ coins: 0, gbp: 0, locked: 0 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    fullLegalName: '',
    method: 'bank',
    bankName: '',
    accountNumber: '',
    sortCode: '',
    paypalEmail: '',
    coins: MIN_COINS,
    confirmed: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [sRes, hRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/writer/stats`),
        authFetch(`${BACKEND_URL}/api/writer/payouts`),
      ]);
      const s = await sRes.json();
      const h = await hRes.json();
      setBalance({
        coins: Number(s.coinBalance || 0),
        gbp: Number(s.gbpBalance || 0),
        locked: Number(s.coinBalanceLocked || 0),
      });
      setHistory(h.requests || []);
    } catch (e) {
      console.error('Failed to load earnings', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const coinsInt = Number(form.coins) || 0;
  const gbpForCoins = useMemo(() => (coinsInt / 100).toFixed(2), [coinsInt]);

  // Inline validation — mirrors server rules so errors surface instantly.
  const coinError = (() => {
    if (coinsInt < MIN_COINS) return `Minimum withdrawal is ${MIN_COINS} coins (£${(MIN_COINS / 100).toFixed(2)}).`;
    if (coinsInt > MAX_COINS) return `Maximum per request is ${MAX_COINS} coins (£${(MAX_COINS / 100).toFixed(2)}).`;
    if (coinsInt > balance.coins) return 'Insufficient coin balance.';
    return '';
  })();

  const methodValid = (() => {
    if (form.method === 'bank') {
      const digits = (form.accountNumber || '').replace(/\D/g, '');
      const sc = (form.sortCode || '').replace(/\D/g, '');
      return !!form.bankName.trim() && digits.length === 8 && sc.length === 6;
    }
    return /.+@.+\..+/.test((form.paypalEmail || '').trim());
  })();

  const canSubmit =
    !!form.fullLegalName.trim()
    && !coinError
    && methodValid
    && form.confirmed
    && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/writer/payouts/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          coins: coinsInt,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(`Payout request ${data.requestId} submitted.`);
      setForm({
        fullLegalName: '',
        method: 'bank',
        bankName: '',
        accountNumber: '',
        sortCode: '',
        paypalEmail: '',
        coins: MIN_COINS,
        confirmed: false,
      });
      load();
    } catch (e) {
      toast.error(e.message || 'Payout submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-gray-400 text-sm flex items-center gap-2 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your earnings…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="earnings-payout-tab">
      {/* Balance tile */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
              <Coins className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-gray-400">Available to withdraw</p>
              <p className="text-2xl font-extrabold text-white" data-testid="earnings-available-coins">
                {balance.coins.toLocaleString()} <span className="text-sm text-gray-400 font-normal">coins</span>
              </p>
              <p className="text-xs text-emerald-300">£{balance.gbp.toFixed(2)} GBP</p>
            </div>
          </div>
          {balance.locked > 0 && (
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-wider text-gray-400">Locked</p>
              <p className="text-lg font-bold text-amber-300" data-testid="earnings-locked-coins">
                {balance.locked.toLocaleString()} coins
              </p>
              <p className="text-[11px] text-gray-500">£{(balance.locked / 100).toFixed(2)} held in pending requests</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Withdrawal form */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-400" /> Request a payout
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-testid="payout-form">
          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Full legal name</label>
            <Input
              value={form.fullLegalName}
              onChange={(e) => setForm({ ...form, fullLegalName: e.target.value })}
              placeholder="Must match your bank account name"
              className="bg-slate-900 border-slate-700 text-white"
              data-testid="payout-full-name"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Payout method</label>
            <div className="grid grid-cols-2 gap-2">
              {['bank', 'paypal'].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setForm({ ...form, method: m })}
                  data-testid={`payout-method-${m}`}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                    form.method === m
                      ? 'bg-emerald-600/20 border-emerald-500/50 text-emerald-100'
                      : 'bg-slate-900/60 border-slate-700 text-gray-300 hover:border-slate-500'
                  }`}
                >
                  {m === 'bank' ? <Banknote className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
                  {m === 'bank' ? 'Bank Transfer (UK)' : 'PayPal'}
                </button>
              ))}
            </div>
          </div>

          {form.method === 'bank' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-300 mb-1">Bank name</label>
                <Input
                  value={form.bankName}
                  onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="payout-bank-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">Account number (8 digits)</label>
                <Input
                  value={form.accountNumber}
                  onChange={(e) => setForm({ ...form, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                  inputMode="numeric"
                  maxLength={8}
                  className="bg-slate-900 border-slate-700 text-white font-mono"
                  data-testid="payout-account-number"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">Sort code (XX-XX-XX)</label>
                <Input
                  value={form.sortCode}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                    const formatted = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 6)]
                      .filter(Boolean)
                      .join('-');
                    setForm({ ...form, sortCode: formatted });
                  }}
                  placeholder="12-34-56"
                  maxLength={8}
                  className="bg-slate-900 border-slate-700 text-white font-mono"
                  data-testid="payout-sort-code"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">PayPal email</label>
              <Input
                type="email"
                value={form.paypalEmail}
                onChange={(e) => setForm({ ...form, paypalEmail: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="payout-paypal-email"
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">Coins to withdraw</label>
              <Input
                type="number"
                min={MIN_COINS}
                max={MAX_COINS}
                value={form.coins}
                onChange={(e) => setForm({ ...form, coins: e.target.value })}
                className={`bg-slate-900 border-slate-700 text-white font-mono ${coinError ? 'border-rose-500/60' : ''}`}
                data-testid="payout-coins"
              />
              {coinError && (
                <p className="text-[11px] text-rose-300 mt-1 flex items-start gap-1" data-testid="payout-coin-error">
                  <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" /> {coinError}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">GBP equivalent</label>
              <div
                className="px-3 py-2 bg-slate-900/60 border border-slate-700 rounded-md text-emerald-300 font-bold font-mono"
                data-testid="payout-gbp-equivalent"
              >
                £{gbpForCoins}
              </div>
            </div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.confirmed}
              onChange={(e) => setForm({ ...form, confirmed: e.target.checked })}
              className="mt-1 w-4 h-4 accent-emerald-500"
              data-testid="payout-confirm"
            />
            <span className="text-xs text-gray-300">
              I confirm my payment details are correct and authorise this payout request.
              Incorrect details may result in payment delays.
            </span>
          </label>

          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50"
            data-testid="payout-submit"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>) : 'Submit payout request'}
          </Button>
          <p className="text-[11px] text-gray-500 text-center">
            Processing time after approval: 5–10 business days.
          </p>
        </CardContent>
      </Card>

      {/* History */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-white">Request history</CardTitle>
          <button
            type="button"
            onClick={load}
            className="text-gray-400 hover:text-white"
            title="Refresh"
            data-testid="payout-history-refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-gray-400">No payout requests yet.</p>
          ) : (
            <ul className="space-y-2" data-testid="payout-history">
              {history.map((r) => (
                <li
                  key={r.requestId}
                  className="p-3 bg-slate-900/40 rounded-lg border border-slate-700 flex items-center justify-between gap-3 flex-wrap"
                  data-testid={`payout-row-${r.requestId}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <StatusPill status={r.status} />
                      <span className="text-[11px] text-gray-400">
                        {new Date(r.requestDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="text-sm text-white font-semibold">
                      {r.coinsWithdrawn?.toLocaleString()} coins · £{Number(r.gbpEquivalent || 0).toFixed(2)}
                    </div>
                    <div className="text-[11px] text-gray-400">
                      {r.method === 'bank'
                        ? `Bank · ${r.details?.accountNumberMasked || '••••'}`
                        : `PayPal · ${r.details?.paypalEmailMasked || '•••'}`}
                    </div>
                    {r.rejectionReason && (
                      <p className="text-[11px] text-rose-300 mt-1" data-testid={`payout-reason-${r.requestId}`}>
                        Reason: {r.rejectionReason}
                      </p>
                    )}
                  </div>
                  <span className="font-mono text-[10px] text-gray-500">{r.requestId}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EarningsAndPayout;
